import React, { createContext, useContext, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthModal, AuthMode } from './AuthModal';

interface AuthModalContextType {
  isOpen: boolean;
  mode: AuthMode;
  openAuth: (mode?: AuthMode, targetPlan?: string) => void;
  closeAuth: () => void;
  switchMode: (mode: AuthMode) => void;
  handleAuthNavigate: (tab: string, targetPlan?: string) => boolean;
}

const AuthModalContext = createContext<AuthModalContextType | undefined>(undefined);

export function isDesktopWidth(): boolean {
  if (typeof window === 'undefined') return true;
  return window.innerWidth >= 768;
}

export const AuthModalProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [mode, setMode] = useState<AuthMode>('login');
  const navigate = useNavigate();

  const openAuth = useCallback((targetMode: AuthMode = 'login', targetPlan?: string) => {
    if (isDesktopWidth()) {
      setMode(targetMode);
      setIsOpen(true);
    } else {
      const planQuery = targetPlan ? `?plan=${encodeURIComponent(targetPlan)}` : '';
      navigate(`/${targetMode}${planQuery}`);
    }
  }, [navigate]);

  const closeAuth = useCallback(() => {
    setIsOpen(false);
  }, []);

  const switchMode = useCallback((newMode: AuthMode) => {
    setMode(newMode);
  }, []);

  /**
   * Intercepte la navigation auth (login / register / forgot) :
   * - Sur grand écran / PC (>= 768px) : ouvre directement le modal sans redirection de page et retourne true
   * - Sur mobile (< 768px) : retourne false pour laisser la navigation classique router vers /login ou /register
   */
  const handleAuthNavigate = useCallback((tab: string, targetPlan?: string): boolean => {
    if (tab === 'login' || tab === 'register' || tab === 'forgot') {
      if (isDesktopWidth()) {
        setMode(tab as AuthMode);
        setIsOpen(true);
        return true;
      }
      return false;
    }
    return false;
  }, []);

  return (
    <AuthModalContext.Provider
      value={{
        isOpen,
        mode,
        openAuth,
        closeAuth,
        switchMode,
        handleAuthNavigate,
      }}
    >
      {children}
      <AuthModal
        isOpen={isOpen}
        mode={mode}
        onClose={closeAuth}
        onSwitchMode={switchMode}
      />
    </AuthModalContext.Provider>
  );
};

export const useAuthModal = () => {
  const ctx = useContext(AuthModalContext);
  if (!ctx) {
    throw new Error('useAuthModal must be used within an AuthModalProvider');
  }
  return ctx;
};
